import React, { useState, useRef, useEffect } from "react";
import { Divider, Modal, Switch, Form, Tag } from "antd";
import { PlusOutlined, DeleteOutlined, EditOutlined, MoreOutlined } from "@ant-design/icons";
import { t } from "i18next";
import { useDispatch, useSelector } from "react-redux";
import centralApi from "../../../../../../services/centralApi";
import useMessage from "hooks/useMessage";
import { getToken } from "redux/features/app/authTokenSlice";
import { API_ENDPOINTS } from "utils/constants";
import Spinner from "../../../../../../components/Spinner/Spinner";
import PrimaryButton from "../../../../../../components/Buttons/PrimaryButton/PrimaryButton";
import TextInput from "../../../../../../components/Inputs/TextInput/TextInput";
import Selector from "../../../../../../components/Selector/Selector";
import "./DynamicRouting.css";
import { CampaignChannelTypeEnum } from "utils/enums";

// ── Constants ──────────────────────────────────────────────────────────────────

const CHANNEL_TABS = [
  { key:CampaignChannelTypeEnum.EMAIL,    label: "Email" },
  { key: CampaignChannelTypeEnum.SMS,      label: "SMS" },
  { key:CampaignChannelTypeEnum.WHATSAPP, label: "WhatsApp" },
];

const RULE_TYPES = [
  { key: "priority",      label: "priorityLabel",         color: "#1677ff", desc: "priorityRuleDescLabel" },
  { key: "traffic_split", label: "trafficSplitLabel",     color: "#52c41a", desc: "trafficSplitRuleDescLabel" },
  { key: "geography",     label: "geographyBasedLabel",   color: "#722ed1", desc: "geographyRuleDescLabel" },
  { key: "performance",   label: "performanceBasedLabel", color: "#fa8c16", desc: "performanceRuleDescLabel" },
  { key: "custom",        label: "customConditionsLabel", color: "#eb2f96", desc: "customRuleDescLabel" },
  { key: "budget_based",  label: "budgetBasedLabel",      color: "#13c2c2", desc: "budgetBasedRuleDescLabel" },
  { key: "cost_based",    label: "costBasedLabel",        color: "#d46b08", desc: "costBasedRuleDescLabel" },
];

const GEO_ATTRIBUTES = ["country_code", "region", "city", "language", "mobile"];
const GEO_OPERATORS  = ["is", "is not", "starts with", "contains"];
const PERF_METRICS   = [
  { value: "delivery_rate", label: "Best Delivery Rate" },
  { value: "latency",       label: "Lowest Latency" },
  { value: "open_rate",     label: "Best Open Rate" },
  { value: "error_rate",    label: "Lowest Error Rate" },
];
const USER_ATTRS     = ["mobile", "country_code", "LTV", "region", "city", "language", "user_segment"];
const API_ATTRS      = ["campaign_type", "priority", "message_type", "region", "sender_id"];
const COND_OPERATORS = ["is", "is not", "starts with", "contains", "exists", "greater than", "less than", "equal to"];
const ALL_ATTR_OPTIONS = [
  ...USER_ATTRS.map(a => ({ value: `user|${a}`, label: `${a} (user)` })),
  ...API_ATTRS.map(a  => ({ value: `api|${a}`,  label: `${a} (API)` })),
];
const CURRENCIES = ["USD", "EUR", "GBP", "INR", "AED", "SGD", "AUD"];
const PERIODS    = [{ value: "daily", label: "Daily" }, { value: "monthly", label: "Monthly" }];

const uid = () => Math.random().toString(36).slice(2, 9);

// ── Default config factories ───────────────────────────────────────────────────

const defaultConfig = (ruleType: string) => {
  switch (ruleType) {
    case "priority":      return { entries: [], fallbackPartnerId: "" };
    case "traffic_split": return { rows: [{ partnerId: "", percent: 100 }], fallbackPartnerId: "" };
    case "geography":     return { rules: [{ attribute: "country_code", operator: "is", value: "", partnerId: "" }], fallbackPartnerId: "" };
    case "performance":   return { metric: "delivery_rate", fallbackPartnerId: "" };
    case "custom":        return {
      blocks: [{ id: uid(), conditions: [{ id: uid(), attrType: "user", attribute: "country_code", operator: "is", value: "", caseSensitive: false }], logic: "AND", thenPartnerId: "" }],
      fallbackPartnerId: "",
    };
    case "budget_based":  return { entries: [], fallbackPartnerId: "" };
    case "cost_based":    return { entries: [], fallbackPartnerId: "" };
    default:              return { fallbackPartnerId: "" };
  }
};

// ── FallbackRow ────────────────────────────────────────────────────────────────

const FallbackRow: React.FC<{ value: any; onChange: any; partners: any[] }> = ({ value, onChange, partners }) => (
  <div className="d-flex align-items-center gap-2 px-3 py-2 rounded border dr-fallback-row">
    <span className="badge text-uppercase fw-bold dr-fallback-badge">Else</span>
    <span className="text-secondary small">use fallback sender as</span>
    <Selector
      value={value || undefined}
      onChange={onChange}
      size="small"
      placeholder="Select fallback"
      options={partners.map(p => ({ value: p.partnerId || p.partnerName, label: p.partnerName }))}
    />
  </div>
);

// ── Partner icon thumbnail ─────────────────────────────────────────────────────

const PartnerIcon: React.FC<{ partner?: any; id?: string }> = ({ partner, id }) => (
  <div className="dr-icon-32 rounded d-flex align-items-center justify-content-center overflow-hidden flex-shrink-0 bg-light">
    {partner?.icon
      ? <img src={partner.icon} alt={partner.partnerName} className="dr-partner-icon-img" />
      : <span className="fw-bold text-secondary dr-text-xs">
          {(partner?.partnerName || id || "?").charAt(0).toUpperCase()}
        </span>
    }
  </div>
);

// ── PriorityForm ───────────────────────────────────────────────────────────────

const PriorityForm: React.FC<{ config: any; setConfig: any; partners: any[] }> = ({ config, setConfig, partners }) => {
  const dragIdx     = useRef<number | null>(null);
  const dragOverIdx = useRef<number | null>(null);
  const [dragging, setDragging] = useState<number | null>(null);
  const [dragOver, setDragOver] = useState<number | null>(null);

  const addedIds  = config.entries.map((e: any) => e.partnerId);
  const available = partners.filter(p => !addedIds.includes(p.partnerId || p.partnerName));
  const getPartner = (id: string) => partners.find(p => (p.partnerId || p.partnerName) === id);

  const add    = (id: string) => { if (!id) return; setConfig({ ...config, entries: [...config.entries, { partnerId: id, active: true }] }); };
  const remove = (idx: number) => setConfig({ ...config, entries: config.entries.filter((_: any, i: number) => i !== idx) });
  const toggle = (idx: number) => setConfig({ ...config, entries: config.entries.map((e: any, i: number) => i === idx ? { ...e, active: !e.active } : e) });
  const move   = (from: number, to: number) => {
    if (to < 0 || to >= config.entries.length) return;
    const arr = [...config.entries];
    [arr[from], arr[to]] = [arr[to], arr[from]];
    setConfig({ ...config, entries: arr });
  };

  const onDragStart = (e: React.DragEvent, idx: number) => {
    dragIdx.current = idx;
    setDragging(idx);
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", String(idx));
  };
  const onDragEnter = (e: React.DragEvent, idx: number) => {
    e.preventDefault();
    dragOverIdx.current = idx;
    setDragOver(idx);
  };
  const onDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  };
  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const from = dragIdx.current;
    const to   = dragOverIdx.current;
    if (from === null || to === null || from === to) {
      dragIdx.current = null; dragOverIdx.current = null;
      setDragging(null); setDragOver(null);
      return;
    }
    const arr = [...config.entries];
    const [moved] = arr.splice(from, 1);
    arr.splice(to, 0, moved);
    setConfig({ ...config, entries: arr });
    dragIdx.current = null; dragOverIdx.current = null;
    setDragging(null); setDragOver(null);
  };
  const onDragEnd = () => {
    setDragging(null);
    setDragOver(null);
    dragIdx.current = null;
    dragOverIdx.current = null;
  };

  return (
    <div className="d-flex flex-column gap-2">
      <p className="mb-0 text-secondary small lh-base">
        Partners are tried <strong>in order</strong>. If a partner fails or is unavailable, the next one is used automatically.
        Drag <span>⠿</span> to reorder, or use ▲ ▼ buttons.
      </p>

      {/* Header */}
      {config.entries.length > 0 && (
        <div className="dr-priority-grid dr-col-header text-secondary">
          <span />
          <span className="dr-icon-34">Rank</span>
          <span>Icon</span>
          <span>Partner</span>
          <span className="text-center">Active</span>
          <span className="text-center">Order</span>
          <span />
        </div>
      )}

      {/* Rows */}
      {config.entries.map((entry: any, idx: number) => {
        const partner    = getPartner(entry.partnerId);
        const isDragging = dragging === idx;
        const isOver     = dragOver === idx && dragging !== idx;
        const rowClass   = [
          "dr-priority-grid border rounded-3 dr-priority-row dr-cursor-grab",
          isDragging ? "dr-priority-row--dragging" : entry.active ? "dr-priority-row--normal" : "dr-priority-row--inactive",
          isOver     ? "dr-priority-row--over" : "",
        ].filter(Boolean).join(" ");

        return (
          <div
            key={entry.partnerId}
            draggable
            onDragStart={e => onDragStart(e, idx)}
            onDragEnter={e => onDragEnter(e, idx)}
            onDragOver={onDragOver}
            onDrop={onDrop}
            onDragEnd={onDragEnd}
            className={`${rowClass} py-2`}
          >
            <span className="text-secondary dr-cursor-grab dr-user-select-none dr-drag-icon text-center">⠿</span>

            <div className="dr-priority-badge d-flex align-items-center justify-content-center text-white priority-badge-blue">
              {idx + 1}
            </div>

            <PartnerIcon partner={partner} id={entry.partnerId} />

            <span className="fw-semibold text-truncate small">{partner?.partnerName || entry.partnerId}</span>

            <div className="d-flex justify-content-center">
              <Switch size="small" checked={entry.active} onChange={() => toggle(idx)} />
            </div>

            <div className="d-flex flex-column align-items-center gap-1">
              <button className="btn btn-sm btn-outline-secondary py-0 px-1 lh-1 dr-order-btn-text" onClick={() => move(idx, idx - 1)} disabled={idx === 0} title="Move up">▲</button>
              <button className="btn btn-sm btn-outline-secondary py-0 px-1 lh-1 dr-order-btn-text" onClick={() => move(idx, idx + 1)} disabled={idx === config.entries.length - 1} title="Move down">▼</button>
            </div>

            <button className="btn btn-link p-0 text-danger" onClick={() => remove(idx)} title="Remove">
              <DeleteOutlined />
            </button>
          </div>
        );
      })}

      {/* All-fail notice */}
      {config.entries.length > 1 && (
        <div className="d-flex align-items-center gap-2 px-3 py-2 bg-light rounded small text-secondary">
          <span>ℹ</span>
          <span>If <strong>all partners above fail</strong>, the fallback below is used.</span>
        </div>
      )}

      {/* Available pool */}
      {available.length > 0 && (
        <div className="d-flex align-items-center flex-wrap gap-2">
          <span className="text-secondary small">Add to priority list:</span>
          {available.map((p: any) => (
            <div
              key={p.partnerId || p.partnerName}
              className="d-flex flex-column align-items-center gap-1 px-2 py-2 rounded dr-pool-card-primary dr-cursor-pointer dr-min-w-60"
              onClick={() => add(p.partnerId || p.partnerName)}
              title={`Add ${p.partnerName}`}
            >
              <PartnerIcon partner={p} />
              <span className="fw-semibold text-center text-primary dr-text-xxs dr-max-w-56 dr-lh-12">{p.partnerName}</span>
            </div>
          ))}
        </div>
      )}

      <FallbackRow value={config.fallbackPartnerId} onChange={(v: any) => setConfig({ ...config, fallbackPartnerId: v })} partners={partners} />
    </div>
  );
};

// ── TrafficSplitForm ───────────────────────────────────────────────────────────

const TrafficSplitForm: React.FC<{ config: any; setConfig: any; partners: any[] }> = ({ config, setConfig, partners }) => {
  const total = config.rows.reduce((s: number, r: any) => s + Number(r.percent), 0);

  const getBarColorClass = (total: number) => total === 100 ? "dr-progress-green" : "dr-progress-red";
  const barColorClass    = total === 100 ? "bar-text-green" : total > 100 ? "bar-text-red" : "bar-text-orange";

  const updateRow = (i: number, field: string, val: any) =>
    setConfig({ ...config, rows: config.rows.map((r: any, idx: number) => idx === i ? { ...r, [field]: val } : r) });
  const addRow    = () => setConfig({ ...config, rows: [...config.rows, { partnerId: "", percent: 0 }] });
  const removeRow = (i: number) => setConfig({ ...config, rows: config.rows.filter((_: any, idx: number) => idx !== i) });

  return (
    <div className="d-flex flex-column gap-3">
      {config.rows.map((row: any, i: number) => (
        <div key={i} className="d-flex align-items-center flex-wrap gap-2">
          <span className="text-secondary small">Send</span>
          <TextInput
            type="number" min={0} max={100}
            value={String(row.percent)}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => updateRow(i, "percent", Number(e.target.value))}
            className="dr-input-w70"
          />
          <span className="text-secondary small">% of traffic to</span>
          <Selector
            value={row.partnerId || undefined}
            onChange={(v: any) => updateRow(i, "partnerId", v)}
            size="small"
            placeholder="Select partner"
            options={partners.map(p => ({ value: p.partnerId || p.partnerName, label: p.partnerName }))}
          />
          {config.rows.length > 1 && (
            <button className="btn btn-link p-0 text-danger" onClick={() => removeRow(i)}><DeleteOutlined /></button>
          )}
        </div>
      ))}

      {/* Progress bar */}
      <div>
        <div className="d-flex justify-content-between small mb-1">
          <span className="text-uppercase fw-bold text-secondary dr-dist-label">Distribution</span>
          <span className={`fw-bold ${barColorClass}`}>{total}%</span>
        </div>
        <div className="rounded dr-progress-track">
          <div className={`rounded dr-progress-bar ${getBarColorClass(total)}`} />
        </div>
        {total !== 100 && <p className="mb-0 mt-1 text-warning small">⚠ Total must equal exactly 100%</p>}
      </div>

      <PrimaryButton className=" d-inline-flex align-items-center gap-1 " onClick={addRow}>
        <PlusOutlined /> Add partner
      </PrimaryButton>

      <FallbackRow value={config.fallbackPartnerId} onChange={(v: any) => setConfig({ ...config, fallbackPartnerId: v })} partners={partners} />
    </div>
  );
};

// ── GeographyForm ──────────────────────────────────────────────────────────────

const GeographyForm: React.FC<{ config: any; setConfig: any; partners: any[] }> = ({ config, setConfig, partners }) => {
  const updateRule = (i: number, field: string, val: string) =>
    setConfig({ ...config, rules: config.rules.map((r: any, idx: number) => idx === i ? { ...r, [field]: val } : r) });
  const addRule    = () => setConfig({ ...config, rules: [...config.rules, { attribute: "country_code", operator: "is", value: "", partnerId: "" }] });
  const removeRule = (i: number) => setConfig({ ...config, rules: config.rules.filter((_: any, idx: number) => idx !== i) });

  return (
    <div className="d-flex flex-column gap-2">
      {config.rules.map((rule: any, i: number) => (
        <div key={i} className="border rounded overflow-hidden">
          <div className="d-flex align-items-center justify-content-between px-3 py-2 border-bottom bg-primary bg-opacity-10">
          <span className="fw-bold text-uppercase small text-primary">If / Else If</span>
            {config.rules.length > 1 && <button className="btn btn-link btn-sm p-0 text-danger" onClick={() => removeRule(i)}>Remove</button>}
          </div>
          <div className="d-flex align-items-center flex-wrap gap-2 px-3 py-2">
            <Selector value={rule.attribute} onChange={(v: any) => updateRule(i, "attribute", v)} size="small" options={GEO_ATTRIBUTES.map(a => ({ value: a, label: a }))} />
            <Selector value={rule.operator}  onChange={(v: any) => updateRule(i, "operator", v)}  size="small" options={GEO_OPERATORS.map(o => ({ value: o, label: o }))} />
            <TextInput value={rule.value} onChange={(e: React.ChangeEvent<HTMLInputElement>) => updateRule(i, "value", e.target.value)} placeholder="e.g. US" className="dr-input-w90" />
            <span className="text-secondary small">→ use</span>
            <Selector value={rule.partnerId || undefined} onChange={(v: any) => updateRule(i, "partnerId", v)} size="small" placeholder="Select partner" options={partners.map(p => ({ value: p.partnerId || p.partnerName, label: p.partnerName }))} />
          </div>
        </div>
      ))}

      <PrimaryButton className="d-inline-flex align-items-center gap-1 w-auto" onClick={addRule}>
        <PlusOutlined /> Add condition
      </PrimaryButton>

      <FallbackRow value={config.fallbackPartnerId} onChange={(v: any) => setConfig({ ...config, fallbackPartnerId: v })} partners={partners} />
    </div>
  );
};

// ── PerformanceForm ────────────────────────────────────────────────────────────

const PerformanceForm: React.FC<{ config: any; setConfig: any; partners: any[] }> = ({ config, setConfig, partners }) => (
  <div className="d-flex flex-column gap-3">
    <div className="d-flex align-items-center gap-2">
      <span className="text-secondary small">Route to the partner with the best</span>
      <Selector value={config.metric} onChange={(v: any) => setConfig({ ...config, metric: v })} size="small" options={PERF_METRICS} />
    </div>
    <div className="alert alert-info py-2 px-3 mb-0 small">
      ℹ The platform will automatically evaluate all configured partners and route to the top performer in real time.
    </div>
    <FallbackRow value={config.fallbackPartnerId} onChange={(v: any) => setConfig({ ...config, fallbackPartnerId: v })} partners={partners} />
  </div>
);

// ── CustomForm ─────────────────────────────────────────────────────────────────

const CustomForm: React.FC<{ config: any; setConfig: any; partners: any[] }> = ({ config, setConfig, partners }) => {
  const mkCond  = () => ({ id: uid(), attrType: "user", attribute: "country_code", operator: "is", value: "", caseSensitive: false });
  const mkBlock = () => ({ id: uid(), conditions: [mkCond()], logic: "AND", thenPartnerId: "" });

  const updBlock = (bid: string, patch: any) =>
    setConfig({ ...config, blocks: config.blocks.map((b: any) => b.id === bid ? { ...b, ...patch } : b) });
  const addBlock = () => setConfig({ ...config, blocks: [...config.blocks, mkBlock()] });
  const delBlock = (bid: string) => setConfig({ ...config, blocks: config.blocks.filter((b: any) => b.id !== bid) });
  const updCond  = (bid: string, cid: string, patch: any) =>
    updBlock(bid, { conditions: config.blocks.find((b: any) => b.id === bid).conditions.map((c: any) => c.id === cid ? { ...c, ...patch } : c) });
  const addCond  = (bid: string) =>
    updBlock(bid, { conditions: [...config.blocks.find((b: any) => b.id === bid).conditions, mkCond()] });
  const delCond  = (bid: string, cid: string) =>
    updBlock(bid, { conditions: config.blocks.find((b: any) => b.id === bid).conditions.filter((c: any) => c.id !== cid) });

  return (
    <div className="d-flex flex-column gap-2">
      {config.blocks.map((block: any, bi: number) => (
        <div key={block.id} className="border rounded overflow-hidden">
          {/* Header */}
          <div className="d-flex align-items-center justify-content-between px-3 py-2 border-bottom bg-primary bg-opacity-10">
            <div className="d-flex align-items-center gap-2">
              <span className="fw-bold text-uppercase small ">
                {bi === 0 ? "If" : "Else If"}
              </span>
              {block.conditions.length > 1 && (
                <div className="btn-group btn-group-sm">
                  {(["AND", "OR"]).map(v => (
                    <button key={v} className={`btn btn-sm py-0 px-2 fw-bold dr-text-xxs ${block.logic === v ? "btn-primary" : "btn-outline-secondary"}`} onClick={() => updBlock(block.id, { logic: v })}>{v}</button>
                  ))}
                </div>
              )}
            </div>
            {config.blocks.length > 1 && <button className="btn btn-link btn-sm p-0 text-danger" onClick={() => delBlock(block.id)}>Remove</button>}
          </div>

          {/* Conditions */}
          <div className="d-flex flex-column gap-2 px-3 py-2 bg-light">
            {block.conditions.map((cond: any, ci: number) => (
              <div key={cond.id}>
                {ci > 0 && (
                  <div className="d-flex align-items-center gap-2 my-1">
                    <hr className="flex-grow-1 my-0" />
                    <span className="text-secondary fw-bold dr-text-xxs">{block.logic}</span>
                    <hr className="flex-grow-1 my-0" />
                  </div>
                )}
                <div className="d-flex align-items-center flex-wrap gap-2">
                  <Selector
                    value={`${cond.attrType}|${cond.attribute}`}
                    onChange={(v: any) => { const [tp, a] = v.split("|"); updCond(block.id, cond.id, { attrType: tp, attribute: a }); }}
                    size="small"
                    options={ALL_ATTR_OPTIONS}
                  />
                  <Selector value={cond.operator} onChange={(v: any) => updCond(block.id, cond.id, { operator: v })} size="small" options={COND_OPERATORS.map(o => ({ value: o, label: o }))} />
                  {cond.operator !== "exists" && (
                    <TextInput value={cond.value} onChange={(e: React.ChangeEvent<HTMLInputElement>) => updCond(block.id, cond.id, { value: e.target.value })} placeholder="value" className="dr-input-w90" />
                  )}
                  <label className="d-flex align-items-center gap-1 text-secondary small mb-0 dr-cursor-pointer">
                    <input type="checkbox" checked={cond.caseSensitive} onChange={e => updCond(block.id, cond.id, { caseSensitive: e.target.checked })} />
                    Case sensitive
                  </label>
                  {block.conditions.length > 1 && (
                    <button className="btn btn-link p-0 text-danger small" onClick={() => delCond(block.id, cond.id)}><DeleteOutlined /></button>
                  )}
                </div>
              </div>
            ))}
            <button className="btn btn-sm btn-outline-secondary d-inline-flex align-items-center gap-1 w-auto" onClick={() => addCond(block.id)}>
              <PlusOutlined /> AND/OR condition
            </button>
          </div>

          {/* THEN */}
          <div className="d-flex align-items-center gap-2 px-3 py-2 border-top dr-then-row">
            <span className="badge fw-bold text-uppercase dr-then-badge">Then use</span>
            <Selector value={block.thenPartnerId || undefined} onChange={(v: any) => updBlock(block.id, { thenPartnerId: v })} size="small" placeholder="Select partner" options={partners.map(p => ({ value: p.partnerId || p.partnerName, label: p.partnerName }))} />
          </div>
        </div>
      ))}

      <PrimaryButton className="d-inline-flex align-items-center gap-1 w-auto" onClick={addBlock}>
        <PlusOutlined /> Add ELSE IF block
      </PrimaryButton>

      <FallbackRow value={config.fallbackPartnerId} onChange={(v: any) => setConfig({ ...config, fallbackPartnerId: v })} partners={partners} />
    </div>
  );
};

// ── BudgetForm ─────────────────────────────────────────────────────────────────

const BudgetForm: React.FC<{ config: any; setConfig: any; partners: any[] }> = ({ config, setConfig, partners }) => {
  const addedIds   = config.entries.map((e: any) => e.partnerId);
  const available  = partners.filter(p => !addedIds.includes(p.partnerId || p.partnerName));
  const getPartner = (id: string) => partners.find(p => (p.partnerId || p.partnerName) === id);

  const addEntry    = (partnerId: string) => { if (!partnerId) return; setConfig({ ...config, entries: [...config.entries, { partnerId, budgetLimit: 0, currency: "USD", period: "monthly" }] }); };
  const updateEntry = (i: number, patch: any) => setConfig({ ...config, entries: config.entries.map((e: any, idx: number) => idx === i ? { ...e, ...patch } : e) });
  const removeEntry = (i: number) => setConfig({ ...config, entries: config.entries.filter((_: any, idx: number) => idx !== i) });

  return (
    <div className="d-flex flex-column gap-3">

      <div className="alert py-2 px-3 mb-0 small dr-alert-teal">
        ℹ Set a max spend cap per partner per period. Once a partner hits its budget, traffic automatically falls over to the next available partner.
      </div>

      {/* Header */}
      {config.entries.length > 0 && (
        <div className="d-flex align-items-center gap-2 px-2 dr-col-header text-secondary">
          <span className="dr-w-32" />
          <span className="flex-grow-1">Partner</span>
          <span className="dr-w-110">Budget Limit</span>
          <span className="dr-w-90">Currency</span>
          <span className="dr-w-100">Period</span>
          <span className="dr-w-32" />
        </div>
      )}

      {/* Entries */}
      {config.entries.map((entry: any, i: number) => {
        const partner = getPartner(entry.partnerId);
        return (
          <div key={entry.partnerId} className="d-flex align-items-center gap-2 px-3 py-2 bg-white border rounded-3 shadow-sm">
            <PartnerIcon partner={partner} id={entry.partnerId} />
            <span className="flex-grow-1 fw-semibold text-truncate small">{partner?.partnerName || entry.partnerId}</span>
            <div className="dr-w-110">
              <TextInput type="number" min={0} value={String(entry.budgetLimit)} onChange={(e: React.ChangeEvent<HTMLInputElement>) => updateEntry(i, { budgetLimit: Number(e.target.value) })} placeholder="e.g. 500" />
            </div>
            <div className="dr-w-90">
              <Selector value={entry.currency} onChange={(v: any) => updateEntry(i, { currency: v })} size="small" options={CURRENCIES.map(c => ({ value: c, label: c }))} />
            </div>
            <div className="dr-w-100">
              <Selector value={entry.period} onChange={(v: any) => updateEntry(i, { period: v })} size="small" options={PERIODS} />
            </div>
            <button className="btn btn-link p-0 text-danger dr-w-32" onClick={() => removeEntry(i)}><DeleteOutlined /></button>
          </div>
        );
      })}

      {/* Available pool */}
      {available.length > 0 && (
        <div className="d-flex align-items-center flex-wrap gap-2">
          <span className="text-secondary small">Add partner:</span>
          {available.map((p: any) => (
            <div
              key={p.partnerId || p.partnerName}
              className="d-flex flex-column align-items-center gap-1 px-2 py-2 rounded dr-pool-card-teal dr-cursor-pointer dr-min-w-60"
              onClick={() => addEntry(p.partnerId || p.partnerName)}
              title={`Add ${p.partnerName}`}
            >
              <PartnerIcon partner={p} />
              <span className="fw-semibold text-center dr-text-xxs dr-max-w-56 dr-lh-12 dr-text-teal">{p.partnerName}</span>
            </div>
          ))}
        </div>
      )}

      <FallbackRow value={config.fallbackPartnerId} onChange={(v: any) => setConfig({ ...config, fallbackPartnerId: v })} partners={partners} />
    </div>
  );
};

// ── CostForm ───────────────────────────────────────────────────────────────────

const CostForm: React.FC<{ config: any; setConfig: any; partners: any[] }> = ({ config, setConfig, partners }) => {
  const addedIds   = config.entries.map((e: any) => e.partnerId);
  const available  = partners.filter(p => !addedIds.includes(p.partnerId || p.partnerName));
  const getPartner = (id: string) => partners.find(p => (p.partnerId || p.partnerName) === id);

  const addEntry    = (partnerId: string) => { if (!partnerId) return; setConfig({ ...config, entries: [...config.entries, { partnerId, costPerMessage: 0, currency: "USD" }] }); };
  const updateEntry = (i: number, patch: any) => setConfig({ ...config, entries: config.entries.map((e: any, idx: number) => idx === i ? { ...e, ...patch } : e) });
  const removeEntry = (i: number) => setConfig({ ...config, entries: config.entries.filter((_: any, idx: number) => idx !== i) });

  const sorted = [...config.entries].sort((a: any, b: any) => a.costPerMessage - b.costPerMessage);

  return (
    <div className="d-flex flex-column gap-3">

      <div className="alert py-2 px-3 mb-0 small dr-alert-orange">
        ℹ Assign a cost per message to each partner. Messages will always be routed to the <strong>cheapest available partner</strong> first.
      </div>

      {/* Header */}
      {config.entries.length > 0 && (
        <div className="d-flex align-items-center gap-2 px-2 dr-col-header text-secondary">
          <span className="dr-w-32" />
          <span className="flex-grow-1">Partner</span>
          <span className="dr-w-130">Cost / Message</span>
          <span className="dr-w-90">Currency</span>
          <span className="dr-w-32" />
        </div>
      )}

      {/* Entries */}
      {config.entries.map((entry: any, i: number) => {
        const partner = getPartner(entry.partnerId);
        return (
          <div key={entry.partnerId} className="d-flex align-items-center gap-2 px-3 py-2 bg-white border rounded-3 shadow-sm">
            <PartnerIcon partner={partner} id={entry.partnerId} />
            <span className="flex-grow-1 fw-semibold text-truncate small">{partner?.partnerName || entry.partnerId}</span>
            <div className="dr-w-130">
              <TextInput type="number" min={0} step={0.001} value={String(entry.costPerMessage)} onChange={(e: React.ChangeEvent<HTMLInputElement>) => updateEntry(i, { costPerMessage: Number(e.target.value) })} placeholder="e.g. 0.005" />
            </div>
            <div className="dr-w-90">
              <Selector value={entry.currency} onChange={(v: any) => updateEntry(i, { currency: v })} size="small" options={CURRENCIES.map(c => ({ value: c, label: c }))} />
            </div>
            <button className="btn btn-link p-0 text-danger dr-w-32" onClick={() => removeEntry(i)}><DeleteOutlined /></button>
          </div>
        );
      })}

      {/* Routing preview */}
      {sorted.length > 1 && (
        <div className="p-3 bg-light border rounded">
          <div className="dr-col-header text-secondary mb-2">Routing order preview (cheapest first)</div>
          <div className="d-flex flex-column gap-1">
            {sorted.map((e: any, i: number) => {
              const p = getPartner(e.partnerId);
              return (
                <div key={e.partnerId} className="d-flex align-items-center gap-2 small">
                  <div className="dr-rank-badge d-flex align-items-center justify-content-center text-white fw-bold rank-badge-blue">
                    {i + 1}
                  </div>
                  <span className="fw-semibold">{p?.partnerName || e.partnerId}</span>
                  <span className="text-secondary">— {e.currency} {e.costPerMessage.toFixed(4)} / msg</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Available pool */}
      {available.length > 0 && (
        <div className="d-flex align-items-center flex-wrap gap-2">
          <span className="text-secondary small">Add partner:</span>
          {available.map((p: any) => (
            <div
              key={p.partnerId || p.partnerName}
              className="d-flex flex-column align-items-center gap-1 px-2 py-2 rounded dr-pool-card-orange dr-cursor-pointer dr-min-w-60"
              onClick={() => addEntry(p.partnerId || p.partnerName)}
              title={`Add ${p.partnerName}`}
            >
              <PartnerIcon partner={p} />
              <span className="fw-semibold text-center dr-text-xxs dr-max-w-56 dr-lh-12 dr-text-orange">{p.partnerName}</span>
            </div>
          ))}
        </div>
      )}

      <FallbackRow value={config.fallbackPartnerId} onChange={(v: any) => setConfig({ ...config, fallbackPartnerId: v })} partners={partners} />
    </div>
  );
};

// ── RuleModal ──────────────────────────────────────────────────────────────────

const RuleModal: React.FC<{ open: any; initialRule: any; channel: any; partners: any[]; onClose: any; onSave: any }> = ({ open, initialRule, channel, partners, onClose, onSave }) => {
  const [step, setStep]         = useState(1);
  const [name, setName]         = useState("");
  const [description, setDesc]  = useState("");
  const [ruleType, setRuleType] = useState("priority");
  const [config, setConfig]     = useState<any>(defaultConfig("priority"));

  useEffect(() => {
    if (initialRule) {
      setStep(1); setName(initialRule.name); setDesc(initialRule.description);
      setRuleType(initialRule.ruleType); setConfig(initialRule.config);
    } else {
      setStep(1); setName(""); setDesc(""); setRuleType("priority"); setConfig(defaultConfig("priority"));
    }
  }, [initialRule, open]);

  const handleRuleTypeChange = (rt: string) => { setRuleType(rt); setConfig(defaultConfig(rt)); };

  const handleSave = () => {
    onSave({ id: initialRule?.id || uid(), name: name.trim(), description: description.trim(), ruleType, channel, active: initialRule?.active ?? true, config });
    onClose();
  };

  const channelPartners = partners.filter(p => p.channel === channel);

  const renderConfigForm = () => {
    switch (ruleType) {
      case "priority":      return <PriorityForm     config={config} setConfig={setConfig} partners={channelPartners} />;
      case "traffic_split": return <TrafficSplitForm config={config} setConfig={setConfig} partners={channelPartners} />;
      case "geography":     return <GeographyForm    config={config} setConfig={setConfig} partners={channelPartners} />;
      case "performance":   return <PerformanceForm  config={config} setConfig={setConfig} partners={channelPartners} />;
      case "custom":        return <CustomForm       config={config} setConfig={setConfig} partners={channelPartners} />;
      case "budget_based":  return <BudgetForm       config={config} setConfig={setConfig} partners={channelPartners} />;
      case "cost_based":    return <CostForm         config={config} setConfig={setConfig} partners={channelPartners} />;
      default:              return null;
    }
  };

  return (
    <Modal open={open} onCancel={onClose} footer={null} width={780} title={initialRule ? "Edit Routing Rule" : "Add Routing Rule"} destroyOnClose>

      {/* Step bar */}
      <div className="d-flex align-items-center gap-2 mb-4 mt-1">
        {["Details", "Configure Rule"].map((s, i) => (
          <React.Fragment key={i}>
            <div className="d-flex align-items-center gap-2">
              <div className={`dr-step-circle rounded-circle d-flex align-items-center justify-content-center fw-bold ${step === i + 1 ? "bg-primary text-white" : step > i + 1 ? "bg-success text-white" : "bg-light text-secondary"}`}>
                {step > i + 1 ? "✓" : i + 1}
              </div>
              <span className={`small ${step === i + 1 ? "fw-bold text-primary" : "text-secondary"}`}>{s}</span>
            </div>
            {i < 1 && <span className="text-secondary">›</span>}
          </React.Fragment>
        ))}
      </div>

      {/* Body */}
      <div className="dr-modal-scroll">
        {step === 1 ? (
          <div className="d-flex flex-column gap-3">
            <div>
              <label className="fw-semibold small d-block mb-1">Rule Name <span className="text-danger">*</span></label>
              <TextInput value={name} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setName(e.target.value)} placeholder="e.g. Route US traffic to Twilio" maxLength={100} />
            </div>
            <div>
              <label className="fw-semibold small d-block mb-1">Description</label>
              <TextInput value={description} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setDesc(e.target.value)} placeholder="Brief description of this rule..." maxLength={200} />
            </div>
            <div>
              <label className="fw-semibold small d-block mb-2">Rule Type</label>
              <div className="d-flex flex-column gap-2">
                {RULE_TYPES.map(rt => {
                  const isSelected = ruleType === rt.key;
                  return (
                    <div
                      key={rt.key}
                      onClick={() => handleRuleTypeChange(rt.key)}
                      className={`d-flex align-items-start gap-3 p-3 rounded border dr-rule-type-option dr-cursor-pointer ${isSelected ? "selected" : ""}`}
                    >
                      <div className={`dr-radio-wrap rounded-circle border d-flex align-items-center justify-content-center bg-white flex-shrink-0 mt-1 ${isSelected ? "selected" : ""}`}>
                        {isSelected && <div className="dr-radio-dot" />}
                      </div>
                      <div>
                        <div className="fw-bold small">{t(rt.label)}</div>
                        <div className="text-secondary dr-rule-desc-text">{t(rt.desc)}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        ) : (
          <div className="d-flex flex-column gap-3">
            <div className="alert alert-info py-2 px-3 mb-0 small">
              ℹ Rules are evaluated <strong>top-down</strong>. First match wins. Only partners configured in the <strong>Partner</strong> tab are available here.
            </div>
            {channelPartners.length === 0 ? (
              <div className="text-center p-4 bg-light rounded border border-dashed text-secondary small">
                No partners configured for {channel} yet. Go to the <strong>Partner</strong> tab to add partners first.
              </div>
            ) : renderConfigForm()}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="d-flex justify-content-between align-items-center mt-4 pt-3 border-top">
        <button className="btn btn-outline-secondary btn-sm" onClick={onClose}>Cancel</button>
        <div className="d-flex gap-2">
          {step === 2 && <button className="btn btn-outline-secondary btn-sm" onClick={() => setStep(1)}>← Back</button>}
          {step === 1
            ? <PrimaryButton size="middle" type="primary" onClick={() => name.trim() && setStep(2)} disabled={!name.trim()}>Next →</PrimaryButton>
            : <PrimaryButton size="middle" type="primary" onClick={handleSave}>{initialRule ? "Update Rule" : "Save Rule"}</PrimaryButton>
          }
        </div>
      </div>
    </Modal>
  );
};

// ── RuleCard ───────────────────────────────────────────────────────────────────

const RuleCard: React.FC<{ rule: any; index: number; onToggle: any; onEdit: any; onDelete: any }> = ({ rule, index, onToggle, onEdit, onDelete }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const rt = RULE_TYPES.find(r => r.key === rule.ruleType) || RULE_TYPES[0];

  useEffect(() => {
    const handler = (e: MouseEvent) => { if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  return (
    <div className="d-flex align-items-center gap-3 px-3 py-2 bg-white border rounded shadow-sm">
      <span className="rounded-circle bg-light d-flex align-items-center justify-content-center fw-bold text-secondary flex-shrink-0 dr-index-badge">{index + 1}</span>

      <Switch size="small" checked={rule.active} onChange={onToggle} />

      <div className="flex-grow-1 overflow-hidden">
        <div className="fw-bold small text-truncate">{rule.name}</div>
        {rule.description && <div className="text-secondary text-truncate dr-text-sm-12">{rule.description}</div>}
      </div>

      <Tag color={rt.color} className="rule-tag">{t(rt.label)}</Tag>
      <Tag color={rule.active ? "success" : "default"} className="rule-tag">{rule.active ? "Active" : "Inactive"}</Tag>

      <div ref={menuRef} className="position-relative">
        <button className="btn btn-sm btn-outline-secondary" onClick={() => setMenuOpen(p => !p)}>
          <MoreOutlined />
        </button>
        {menuOpen && (
          <div className="rule-card-menu-dropdown">
            {[
              { label: "Edit",   icon: <EditOutlined />,   action: onEdit,   color: "menu-item-edit" },
              { label: "Delete", icon: <DeleteOutlined />, action: onDelete, color: "menu-item-delete" },
            ].map(item => (
              <button
                key={item.label}
                onClick={() => { item.action(); setMenuOpen(false); }}
                className={`rule-card-menu-item ${item.color}`}
              >
                {item.icon} {item.label}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// ── DynamicSenderRouting ───────────────────────────────────────────────────────

const DynamicSenderRouting: React.FC<{ configuredPartners: any[] }> = ({ configuredPartners }) => {
  const [activeChannel, setActiveChannel] = useState("EMAIL");
  const [rules, setRules]                 = useState<any[]>([]);
  const [loading, setLoading]             = useState(false);
  const [modalOpen, setModalOpen]         = useState(false);
  const [editingRule, setEditingRule]     = useState<any>(null);

  const { showError, showSuccess } = useMessage();
  const dispatch: any = useDispatch();
  const loginUser     = useSelector((state: any) => state.loginUser);
  const activeApp     = useSelector((state: any) => state.activeApp);
  const appId         = activeApp.appId;
  const api_key       = loginUser.data.api_key;

  const fetchRules = async () => {
    try {
      setLoading(true);
      const data = await centralApi("GET", API_ENDPOINTS.GET_CURRENT_APPKEY, null, { args: JSON.stringify({ app_id: appId }), app_id: appId, api_key });
      setRules(data?.result[0].routingRules || []);
    } catch { showError(t("somethingWrongLabel")); }
    finally { setLoading(false); }
  };

  const saveRules = async (updatedRules: any[]) => {
    try {
      setLoading(true);
      const token = await dispatch(getToken()).unwrap();
      await centralApi("POST", API_ENDPOINTS.SAVE_APP_SETTING_URL, { args: { app_id: appId, routingRules: updatedRules }, app_id: appId, api_key, authToken: token }, null);
      showSuccess(t("sucessfullySavedLabel"));
      setRules(updatedRules);
    } catch { showError(t("somethingWrongLabel")); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchRules(); dispatch(getToken()); }, [appId]);

  const handleSaveRule = (rule: any) => {
    const updated = rules.find(r => r.id === rule.id) ? rules.map(r => r.id === rule.id ? rule : r) : [...rules, rule];
    saveRules(updated);
  };
  const handleToggle = (id: string) => saveRules(rules.map(r => r.id === id ? { ...r, active: !r.active } : r));
  const handleDelete = (id: string) => saveRules(rules.filter(r => r.id !== id));
  const openAdd      = () => { setEditingRule(null); setModalOpen(true); };
  const openEdit     = (rule: any) => { setEditingRule(rule); setModalOpen(true); };

  const channelRules = rules.filter(r => r.channel === activeChannel);

  return (
    <div>
      <h6 className="partner-setting-container-heading">{t("dynamicSenderRoutingLabel")}</h6>
      <p className="partner-setting-form-headings">{t("dynamicRoutingDescText")}</p>
      <Divider className="partner-setting-custom-divider" />

      {loading ? <Spinner /> : (
        <>
          <div className="partner-channel-tabs">
            {CHANNEL_TABS.map(tab => (
              <button key={tab.key} className={`partner-channel-tab${activeChannel === tab.key ? " active" : ""}`} onClick={() => setActiveChannel(tab.key)}>
                {tab.label}
              </button>
            ))}
          </div>

          <div className="alert alert-info d-flex gap-2 py-2 px-3 mb-3 small">
            <span>ℹ</span>
            <span>Rules are evaluated <strong>top-down</strong>. First matching rule determines the sender. Use the ⋯ menu to edit or delete.</span>
          </div>

          {channelRules.length > 0 ? (
            <div className="d-flex flex-column gap-2 mb-3">
              {channelRules.map((rule: any, idx: number) => (
                <RuleCard key={rule.id} rule={rule} index={idx} onToggle={() => handleToggle(rule.id)} onEdit={() => openEdit(rule)} onDelete={() => handleDelete(rule.id)} />
              ))}
            </div>
          ) : (
            <div className="text-center p-5 bg-light rounded border dr-empty-state mb-3">
              <div className="dr-empty-icon mb-2">🔀</div>
              <div className="fw-semibold mb-1">No routing rules for {CHANNEL_TABS.find(tab => tab.key === activeChannel)?.label}</div>
              <div className="text-secondary small">Add a rule to start routing messages automatically.</div>
            </div>
          )}

          <Form.Item>
            <PrimaryButton size="middle" type="primary" onClick={openAdd} className="border-0">
              <PlusOutlined /> {t("addRuleLabel")}
            </PrimaryButton>
          </Form.Item>
        </>
      )}

      <RuleModal open={modalOpen} initialRule={editingRule} channel={activeChannel} partners={configuredPartners} onClose={() => setModalOpen(false)} onSave={handleSaveRule} />
    </div>
  );
};

export default DynamicSenderRouting;